import React, { useState, useEffect, useRef } from 'react';
import '../pages/common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import config from '../config';
import { sanitizeTextInput } from '../sanitize';

const newConversationId = () => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function ChatTutor({ onNavigate }) {
  const [conversationId, setConversationId] = useState(newConversationId);
  const [userId] = useState('12345');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {role, content}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isListening, setIsListening] = useState(false); // Track microphone state
  const [speakingIndex, setSpeakingIndex] = useState(-1);
  const chatEndRef = useRef(null);
  const recognitionRef = useRef(null); // Reference for SpeechRecognition instance

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    // Initialize SpeechRecognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput((prev) => `${prev} ${transcript}`.trim());
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } else {
      console.warn('SpeechRecognition is not supported in this browser.');
    }
  }, []);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setError('');
    setLoading(true);

    // Optimistically add user message
    const newUserMessage = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, newUserMessage]);
    setInput('');

    try {
      const response = await fetch(config.endpoints.chat, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userMessage: sanitizeTextInput(trimmed),
          conversationId,
          metadata: { userId }
        })
      });

      if (!response.ok) {
        const text = await response.text();
        setError('Oops, something went wrong talking to your helper. Try again.');
        console.error('Chat API error:', text);
        return;
      }

      const data = await response.json();

      const assistantText = data.assistantResponse || '';
      if (!assistantText) {
        setError('I could not understand the reply. Please try again.');
        return;
      }

      // Use history from API if present, else just append assistant message
      if (Array.isArray(data.history) && data.history.length > 0) {
        setMessages(
          data.history.map(h => ({
            role: h.role,
            content: h.content
          }))
        );
      } else {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: assistantText }
        ]);
      }
    } catch (e) {
      console.error(e);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setError('');
    setInput('');
    setConversationId(newConversationId());
  };

  const speakText = (text, idx) => {
    if (!('speechSynthesis' in window)) return;
    if (speakingIndex === idx) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(-1);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.85;
    utterance.pitch = 1.1;
    const voices = window.speechSynthesis.getVoices();
    const friendly = voices.find(v => v.lang.startsWith('en') && /zira|samantha|victoria|karen|moira|fiona|female|woman/i.test(v.name));
    if (friendly) utterance.voice = friendly;
    utterance.onstart = () => setSpeakingIndex(idx);
    utterance.onend = () => setSpeakingIndex(-1);
    utterance.onerror = () => setSpeakingIndex(-1);
    window.speechSynthesis.speak(utterance);
  };

  const handleMicClick = () => {
    if (recognitionRef.current) {
      if (isListening) {
        recognitionRef.current.stop();
      } else {
        recognitionRef.current.start();
      }
    }
  };

  return (
    <div className="App">
      <div className="home-container">
        <Header onNavigate={onNavigate} showHomeButton={true} />

        <main className="chat-container">
          <section className="chat-intro">
            <h2 className="chat-title">24/7 LexGoKids agent</h2>
            <p className="chat-description">
              Type your question using simple words. Your helper will answer
              in short, clear sentences. You can ask for spelling, reading,
              or word practice.
            </p>
            <p className="chat-hint">
              Tip: You can say things like “Help me spell ‘elephant’” or
              “Give me an easy spelling game”.
            </p>
          </section>

          <section className="chat-window">
            <div className="chat-messages">
              {messages.length === 0 && (
                <div className="chat-empty">
                  <p>
                    Start by saying <strong>“Hello”</strong> or asking for
                    a spelling activity.
                  </p>
                </div>
              )}

              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={
                    m.role === 'user'
                      ? 'chat-bubble chat-bubble-user'
                      : 'chat-bubble chat-bubble-assistant'
                  }
                >
                  <div className="chat-bubble-role">
                    {m.role === 'user' ? 'You' : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 900, letterSpacing: 1, lineHeight: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}>
                        {[
                          { char: 'L', color: '#FFE94D' },
                          { char: 'e', color: '#FF8DC7' },
                          { char: 'x', color: '#7FFFB2' },
                          { char: 'G', color: '#FFE94D' },
                          { char: 'o', color: '#FF8DC7' },
                          { char: 'K', color: '#7FFFB2' },
                          { char: 'i', color: '#FFE94D' },
                          { char: 'd', color: '#FF8DC7' },
                          { char: 's', color: '#7FFFB2' },
                        ].map(({ char, color }, i) => (
                          <span key={i} style={{
                            color,
                            WebkitTextStroke: '1.5px rgba(0,0,0,0.2)',
                            display: 'inline-block',
                            transform: i % 2 === 0 ? 'translateY(-2px)' : 'translateY(2px)',
                            textShadow: '1px 2px 0px rgba(0,0,0,0.25)',
                            fontFamily: "'Arial Rounded MT Bold', 'Nunito', 'Comic Sans MS', sans-serif",
                          }}>{char}</span>
                        ))}
                        <span style={{ color: '#2c3e50', fontFamily: "'Arial Rounded MT Bold', 'Nunito', 'Comic Sans MS', sans-serif", marginLeft: 4 }}> Agent</span>
                      </span>
                    )}
                    {m.role === 'assistant' && (
                      <button
                        className={`chat-speak-btn${speakingIndex === idx ? ' chat-speak-btn-active' : ''}`}
                        onClick={() => speakText(m.content, idx)}
                        title={speakingIndex === idx ? 'Stop speaking' : 'Listen to this'}
                      >
                        {speakingIndex === idx ? '🔉' : '🔊'}
                      </button>
                    )}
                  </div>
                  <div className="chat-bubble-text">{m.content}</div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {error && <div className="chat-error">{error}</div>}

            <div className="chat-input-area">
              <div className="chat-input-wrapper">
                <textarea
                  className="chat-input"
                  placeholder="Type your question here or click the microphone to speak. Press Enter to send."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={3}
                />
                <button
                  className="chat-mic-icon"
                  type="button"
                  onClick={handleMicClick}
                  title={isListening ? 'Click to stop listening' : 'Click to speak'}
                >
                  {isListening ? '🎙️' : '🎤'}
                </button>
              </div>
              <div className="chat-actions">
                <button
                  className="chat-new-btn"
                  type="button"
                  onClick={handleNewChat}
                >
                  🔄 New Chat
                </button>
                <button
                  className="chat-send-btn"
                  type="button"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                >
                  {loading ? 'Sending…' : 'Send ✨'}
                </button>
              </div>
            </div>
          </section>

        </main>

        <Footer />
      </div>
    </div>
  );
}

export default ChatTutor;